import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Target, Timer, Activity, Zap, TrendingUp, AlertTriangle, Sparkles, Loader2, Rewind, Brain, ListChecks } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import ExamReplay from "@/components/exam/ExamReplay";
import ErrorCategories from "@/components/exam/ErrorCategories";
import ReviewAnswers from "@/components/exam/ReviewAnswers";
import PercentileCard from "@/components/PercentileCard";
import DuelInviteModal from "@/components/DuelInviteModal";
import { Swords } from "lucide-react";

export default function ExamResults() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [answerKey, setAnswerKey] = useState({});
  const [step, setStep] = useState("loading"); // loading | need-key | graded
  const [extracting, setExtracting] = useState(false);
  const [grading, setGrading] = useState(false);
  const [showDuel, setShowDuel] = useState(false);

  const load = async () => {
    const r = await api.get(`/attempts/${attemptId}`);
    setData(r.data);
    if (r.data.attempt.status === "graded" && r.data.attempt.analytics) {
      setStep("graded");
    } else {
      // pre-populate answer key inputs
      const key = {};
      (r.data.exam.structure.questions || []).forEach((q) => {
        key[q.q_id] = r.data.exam.answer_key?.[q.q_id] || "";
      });
      setAnswerKey(key);
      setStep("need-key");
    }
  };

  useEffect(() => { load(); }, [attemptId]);

  const aiExtract = async () => {
    setExtracting(true);
    try {
      const r = await api.post(`/exams/${data.exam.exam_id}/extract-answers`);
      setAnswerKey((k) => ({ ...k, ...r.data.answer_key }));
      toast.success(`AI extracted ${Object.keys(r.data.answer_key).length} answers. Please confirm.`);
    } catch {
      toast.error("AI could not extract answers. Enter manually.");
    } finally {
      setExtracting(false);
    }
  };

  const grade = async () => {
    setGrading(true);
    try {
      const clean = {};
      Object.entries(answerKey).forEach(([k, v]) => { if (v && v.toString().trim()) clean[k] = v.toString().trim(); });
      if (Object.keys(clean).length === 0) { toast.error("Enter at least some answers"); setGrading(false); return; }
      await api.post(`/attempts/${attemptId}/grade`, { answer_key: clean });
      await load();
      toast.success("Report ready!");
    } catch (e) {
      toast.error("Grading failed");
    } finally {
      setGrading(false);
    }
  };

  if (step === "loading" || !data) {
    return <AppLayout><div className="text-stone-500">Loading…</div></AppLayout>;
  }

  const questions = data.exam.structure.questions;

  if (step === "need-key") {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-6 fade-up">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Almost there</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">Provide the answer key</h1>
            <p className="text-stone-500 mt-2">
              Let AI extract answers from your PDF, or enter them manually. You'll confirm before we grade.
            </p>
          </div>

          <Card className="p-6 rounded-md border-stone-200 shadow-none">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-heading text-lg font-semibold">Answer key</div>
                <div className="text-xs text-stone-500 mt-1">MCQ: A/B/C/D. Multi-correct MCQ: comma-list e.g. <span className="font-mono">A,C</span>. Integer/one-word: exact value.</div>
              </div>
              <Button variant="outline" onClick={aiExtract} disabled={extracting} data-testid="ai-extract-btn">
                {extracting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                AI extract from PDF
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto pr-2">
              {questions.map((q) => (
                <div key={q.q_id} className="flex items-center gap-2 border border-stone-200 rounded-md p-2">
                  <span className="font-mono text-xs text-stone-500 w-8">Q{q.number}</span>
                  <Input
                    value={answerKey[q.q_id] || ""}
                    onChange={(e) => setAnswerKey({ ...answerKey, [q.q_id]: e.target.value })}
                    className="h-8 text-sm font-mono"
                    placeholder={q.q_type === "mcq" ? "A" : q.q_type === "mcq_multi" ? "A,C" : "..."}
                    data-testid={`key-${q.q_id}`}
                  />
                </div>
              ))}
            </div>
          </Card>

          <div className="flex justify-end">
            <Button onClick={grade} disabled={grading} data-testid="grade-btn" className="bg-orange-600 hover:bg-orange-700 text-white h-11">
              {grading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Grade & generate report
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Graded view
  const a = data.attempt.analytics;
  const pct = a.max_score ? Math.round((a.score / a.max_score) * 100) : 0;

  const secData = Object.entries(a.section_stats).map(([k, s]) => ({
    section: k.slice(0, 12),
    correct: s.correct,
    wrong: s.wrong,
    unattempted: s.unattempted,
  }));

  const radarData = [
    { m: "Accuracy", v: a.accuracy },
    { m: "Speed", v: a.speed },
    { m: "Selection", v: a.question_selection },
    { m: "Decision", v: a.decision_quality },
    { m: "Composure", v: Math.max(0, 100 - a.pressure_index) },
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-8 fade-up">
        <div className="flex items-end justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Report</div>
            <h1 className="font-heading text-4xl font-bold tracking-tight">{data.attempt.exam_title}</h1>
            <p className="text-stone-500 mt-2 font-mono text-xs">
              {new Date(data.attempt.submitted_at).toLocaleString()} · {Math.round(a.time_used_seconds / 60)}m used
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowDuel(true)}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="open-duel-btn"
            >
              <Swords className="h-4 w-4 mr-2" /> Duel a friend
            </Button>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
          </div>
        </div>

        <Tabs defaultValue="report" className="space-y-6">
          <TabsList data-testid="results-tabs">
            <TabsTrigger value="report" data-testid="tab-report"><TrendingUp className="h-4 w-4 mr-2" />Report</TabsTrigger>
            <TabsTrigger value="review" data-testid="tab-review"><ListChecks className="h-4 w-4 mr-2" />Review answers</TabsTrigger>
            <TabsTrigger value="replay" data-testid="tab-replay"><Rewind className="h-4 w-4 mr-2" />Replay</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai"><Brain className="h-4 w-4 mr-2" />AI analysis</TabsTrigger>
          </TabsList>

          <TabsContent value="report" className="space-y-8">
        {/* Score hero */}
        <Card className="p-8 rounded-md border-stone-200 shadow-none grid-bg">
          <div className="grid md:grid-cols-4 gap-8 items-center">
            <div className="md:col-span-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">Final score</div>
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-6xl font-bold text-orange-600 tabular-nums">{a.score}</span>
                <span className="font-mono text-2xl text-stone-400">/{a.max_score}</span>
              </div>
              <div className="mt-4 font-heading text-3xl font-bold tabular-nums">{pct}%</div>
            </div>
            <div className="md:col-span-3 grid grid-cols-3 md:grid-cols-4 gap-6">
              <BigStat label="Correct" value={a.correct} accent="text-emerald-600" />
              {a.partial > 0 && (
                <BigStat label="Partial" value={a.partial} accent="text-amber-600" />
              )}
              <BigStat label="Wrong" value={a.wrong} accent="text-rose-600" />
              <BigStat label="Skipped" value={a.unattempted} accent="text-stone-500" />
            </div>
          </div>
          {a.common_mistake && (
            <div className="mt-6 p-4 border-l-4 border-amber-500 bg-amber-50 rounded-r-md flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-800 mb-1">Coach note</div>
                <div className="text-sm text-stone-900">{a.common_mistake}</div>
              </div>
            </div>
          )}
        </Card>

        {/* Metric grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Metric icon={Target} label="Accuracy" v={`${a.accuracy}%`} accent="text-emerald-600" />
          <Metric icon={Timer} label="Speed" v={`${a.speed}`} accent="text-orange-600" />
          <Metric icon={Activity} label="Attempt rate" v={`${a.attempt_rate}%`} accent="text-stone-900" />
          <Metric icon={Zap} label="Decision quality" v={`${a.decision_quality}`} accent="text-orange-600" />
          <Metric icon={AlertTriangle} label="Pressure index" v={`${a.pressure_index}`} accent="text-rose-600" />
          <Metric icon={TrendingUp} label="Selection" v={`${a.question_selection}`} accent="text-emerald-600" />
        </div>

        <PercentileCard attemptId={attemptId} />

        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 rounded-md border-stone-200 shadow-none">
            <h3 className="font-heading text-xl font-semibold mb-1">Section breakdown</h3>
            <p className="text-xs text-stone-500 mb-4 font-mono uppercase tracking-[0.2em]">
              Weakest: {a.weakest_section || "—"} · Strongest: {a.strongest_section || "—"}
            </p>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={secData}>
                  <CartesianGrid stroke="#e7e5e4" strokeOpacity={0.5} vertical={false} />
                  <XAxis dataKey="section" tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                  <YAxis tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="correct" stackId="a" fill="#059669" name="Correct" />
                  <Bar dataKey="wrong" stackId="a" fill="#e11d48" name="Wrong" />
                  <Bar dataKey="unattempted" stackId="a" fill="#a8a29e" name="Skipped" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6 rounded-md border-stone-200 shadow-none">
            <h3 className="font-heading text-xl font-semibold mb-4">Performance radar</h3>
            <div className="h-64">
              <ResponsiveContainer>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e7e5e4" />
                  <PolarAngleAxis dataKey="m" tick={{ fill: "#78716c", fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#a8a29e", fontSize: 10 }} axisLine={false} />
                  <Radar dataKey="v" stroke="#ea580c" fill="#ea580c" fillOpacity={0.3} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <Card className="p-6 rounded-md border-stone-200 shadow-none">
          <h3 className="font-heading text-xl font-semibold mb-4">Behavioral telemetry</h3>
          <div className="grid md:grid-cols-4 gap-6">
            <Stat title="Time wasted" v={`${Math.round(a.time_wasted_seconds / 60)}m ${Math.round(a.time_wasted_seconds % 60)}s`} />
            <Stat title="Avg time/question" v={`${a.avg_time_per_q_seconds}s`} />
            <Stat title="Answer changes" v={`${a.answer_changes}`} />
            <Stat title="Correct → Wrong" v={`${a.correct_to_wrong_changes}`} highlight={a.correct_to_wrong_changes > 0} />
          </div>
        </Card>
          </TabsContent>

          <TabsContent value="review">
            <ReviewAnswers attemptId={attemptId} />
          </TabsContent>

          <TabsContent value="replay">
            <ExamReplay attemptId={attemptId} />
          </TabsContent>

          <TabsContent value="ai">
            <ErrorCategories attemptId={attemptId} exam={data.exam} />
          </TabsContent>
        </Tabs>
      </div>
      <DuelInviteModal
        open={showDuel}
        onOpenChange={setShowDuel}
        attemptId={attemptId}
        examTitle={data.attempt.exam_title}
      />
    </AppLayout>
  );
}

function BigStat({ label, value, accent }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1">{label}</div>
      <div className={`font-heading text-4xl font-bold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}
function Metric({ icon: Icon, label, v, accent }) {
  return (
    <Card className="p-4 rounded-md border-stone-200 shadow-none">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className={`font-heading text-2xl font-bold tabular-nums ${accent}`}>{v}</div>
    </Card>
  );
}
function Stat({ title, v, highlight }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1">{title}</div>
      <div className={`font-mono text-xl font-semibold ${highlight ? "text-rose-600" : "text-stone-900"}`}>{v}</div>
    </div>
  );
}
